import * as Sentry from '@sentry/nextjs';
import type { AxiosRequestConfig } from 'axios';
import axios from 'axios';

import { shouldAvoidCdn } from './cdn';

const HTTP_SUCCESS_MIN = 200;
const HTTP_SUCCESS_MAX = 300;

const API_HOST_PREFIX = /^https?:\/\/[^/]+/i;
const API_QUERY_SUFFIX = /[?#].*$/;
const UUID_SEGMENT =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const NUMERIC_SEGMENT = /\/\d+(?=\/|$)/g;

/**
 * Reduce an API request URL to a low-cardinality route label so Sentry can
 * group and count 500s by endpoint. Strips the host and query string and
 * replaces per-resource ids (numeric segments, UUIDs) with `:id`.
 */
export const normalizeApiEndpoint = (url: string | undefined): string => {
  if (!url) {
    return 'unknown';
  }

  return url
    .replace(API_HOST_PREFIX, '')
    .replace(API_QUERY_SUFFIX, '')
    .replace(UUID_SEGMENT, ':id')
    .replace(NUMERIC_SEGMENT, '/:id');
};

/** Checkout endpoints, where a 4xx is a lost sale rather than routine noise. */
const CHECKOUT_ENDPOINT = /subscription-stripe/;

/**
 * Which failed responses are worth a Sentry event. 4xx is normally expected
 * traffic (401 on logged-out reads, 404s, 429s) so it stays unreported, but a
 * rejected checkout has to be diagnosable without the customer sending us a
 * console screenshot (DEV-6882).
 */
export const shouldReportApiError = (
  status: number | undefined,
  url: string
): boolean =>
  status === 500 ||
  (status !== undefined && status >= 400 && CHECKOUT_ENDPOINT.test(url));

// Shared response interceptor: report server errors and checkout failures to
// Sentry. Never send request body — it may contain passwords, tokens, or PII.
const sentryErrorInterceptor = [
  (response: any) => response,
  (error: any) => {
    const status: number | undefined = error.response?.status;
    const url: string = error.config?.url ?? '';

    if (shouldReportApiError(status, url)) {
      Sentry.captureException(error, {
        // Tags are indexed, so failures can be grouped/counted by endpoint in
        // Sentry; keep them low-cardinality (ids stripped). `extra` keeps the
        // raw url/method for debugging an individual event.
        tags: {
          api_endpoint: normalizeApiEndpoint(url),
          api_method: (error.config?.method ?? '').toUpperCase() || 'unknown',
          api_status: String(status),
        },
        extra: {
          url,
          method: error.config?.method,
          // Only for checkout: the backend's rejection reason, which is the
          // one thing that says *why* the sale failed. Deliberately not shown
          // to the user — the on-screen copy stays generic.
          ...(CHECKOUT_ENDPOINT.test(url)
            ? { response: error.response?.data }
            : {}),
        },
      });
    }

    return Promise.reject(error);
  },
] as const;

// --- V2 API client (used by Orval-generated hooks) ---

const backendApiInstance = axios.create({
  withCredentials: true,
  validateStatus: (status) =>
    status >= HTTP_SUCCESS_MIN && status < HTTP_SUCCESS_MAX,
});

const apiRegex = /^\/api\//;
const apiSegmentRegex = /\/api\//;

/**
 * Resolve an Orval-style `/api/v1/...` path to the URL the V2 client
 * actually requests. Exported for callers that must bypass axios — e.g.
 * keepalive fetches flushed on unmount, which the browser completes even
 * after navigation would abort an XHR-based request.
 */
export const resolveBackendApiUrl = (url: string): string => {
  // The dev /api/v2 proxy is relative, which Node can't resolve during SSR.
  if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
    return `/api/v2/${url.replace(apiRegex, '')}`;
  }
  const apiV2 = process.env.NEXT_PUBLIC_API_V2;
  if (!apiV2) {
    throw new Error(
      'NEXT_PUBLIC_API_V2 is not set. The V2 backend client cannot make ' +
        'requests without it. Configure the env var on the deployment target.'
    );
  }
  return `${apiV2.replace(apiSegmentRegex, '')}${url}`;
};

backendApiInstance.interceptors.request.use((config) => {
  config.url = resolveBackendApiUrl(config.url ?? '');

  return config;
});

backendApiInstance.interceptors.response.use(...sentryErrorInterceptor);

export const backendApi = {
  get: <T = any>(url: string, config?: AxiosRequestConfig) =>
    backendApiInstance.get<T>(url, config),

  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    backendApiInstance.post<T>(url, data, config),

  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    backendApiInstance.put<T>(url, data, config),

  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    backendApiInstance.patch<T>(url, data, config),

  delete: <T = any>(url: string, config?: AxiosRequestConfig) =>
    backendApiInstance.delete<T>(url, config),
};

// --- V1 API client (legacy endpoints not yet on V2) ---

// The two header names V1 endpoints accept a per-user credential under.
const AUTH_HEADERS = ['X-Auth-Token', 'token'];

/**
 * Does this request carry a per-user credential? Such responses must never go
 * via the CDN: it keys on URL alone and the origin sets no `Vary` for these
 * headers, so one user's response gets served to every other user — or a
 * pre-cancellation one back to its owner (DEV-6506).
 */
export const isAuthenticatedRequest = (headers: Record<string, unknown> = {}) =>
  AUTH_HEADERS.some((name) => Boolean(headers[name]));

const v1ApiInstance = axios.create({
  withCredentials: true,
});

v1ApiInstance.interceptors.request.use((config) => {
  const avoidCdn =
    shouldAvoidCdn() ||
    config.headers['X-Skip-CDN'] === 'true' ||
    isAuthenticatedRequest(config.headers);
  const isCachable = ['GET', 'HEAD'].includes(
    config.method?.toUpperCase() ?? ''
  );

  config.baseURL =
    !isCachable || avoidCdn
      ? process.env.NEXT_PUBLIC_API
      : process.env.NEXT_PUBLIC_CDN_API;

  if (avoidCdn) {
    config.headers['Cache-Control'] =
      'no-store, no-cache, must-revalidate, proxy-revalidate, max-age=0';
    config.headers.Pragma = 'no-cache';
    config.headers.Expires = '0';
    delete config.headers['X-Skip-CDN'];
  }

  return config;
});

v1ApiInstance.interceptors.response.use(...sentryErrorInterceptor);

export const v1Api = {
  get: <T = any>(url: string, config?: AxiosRequestConfig) =>
    v1ApiInstance.get<T>(url, config),

  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    v1ApiInstance.post<T>(url, data, config),

  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    v1ApiInstance.put<T>(url, data, config),

  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    v1ApiInstance.patch<T>(url, data, config),

  delete: <T = any>(url: string, config?: AxiosRequestConfig) =>
    v1ApiInstance.delete<T>(url, config),
};
